import axios from "axios";
import utils from "../../utils";
import i18n from "@/plugins/i18n";
import { DateTime } from "luxon";

const outputs = {
  pdf: {
    extension: ".pdf",
    mimetype: "application/pdf"
  },
  xlsx: {
    extension: ".xlsx",
    mimetype: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  }
};

const formatDateTime = function(date, time) {
  if (time === null || time === undefined)
    return DateTime.fromISO(date).toFormat(utils.API_DFORMAT);
  else
    return DateTime.fromISO(date + "T" + time).toFormat(utils.API_DTFORMAT);
};

const defs = {
  logbook: {
    name: "logbook",
    url: function(params) {
      return `logbook/${params.vehicle.vehicleId}`
        + `?from=${encodeURIComponent(formatDateTime(params.period.from, params.period.fromTime))}`
        + `&to=${encodeURIComponent(formatDateTime(params.period.to, params.period.toTime))}`
        + `&detailed=${params.detailed}`
        + `&daySummary=${params.daySummary}`
        + `&weekSummary=${params.weekSummary}`
        + `&borderCrossing=${params.borderCrossing}`;
    },
    filename: function(params) {
      return i18n.t("report.logbook.filename") + `-${params.vehicle.numericPlate}-${params.period.text}`;
    }
  },

  performance: {
    name: "performance",
    url: function(params) {
      return `performance/${params.vehicle.vehicleId}`
        + `?from=${encodeURIComponent(formatDateTime(params.period.from, params.period.fromTime))}`
        + `&to=${encodeURIComponent(formatDateTime(params.period.to, params.period.toTime))}`
        + `&detailed=${params.detailed}`
        + `&daySummary=${params.daySummary}`
        + `&weekSummary=${params.weekSummary}`
        + `&borderCrossing=${params.borderCrossing}`
        + `&categorySummary=${params.categorySummary}`
        + `&showNote=${params.showNote}`;
    },
    filename: function(params) {
      return i18n.t("report.performance.filename") + `-${params.vehicle.numericPlate}-${params.period.text}`;
    }
  },

  "machine-performance": {
    name: "machine_performance",
    url: function(params) {
      return `machine-performance/${params.vehicle.vehicleId}`
        + `?from=${encodeURIComponent(formatDateTime(params.period.from, params.period.fromTime))}`
        + `&to=${encodeURIComponent(formatDateTime(params.period.to, params.period.toTime))}`
        + `&detailed=${params.detailed}`
        + `&daySummary=${params.daySummary}`
        + `&weekSummary=${params.weekSummary}`
        + `&borderCrossing=${params.borderCrossing}`;
    },
    filename: function(params) {
      return i18n.t("report.machine-performance.filename") + `-${params.vehicle.numericPlate}-${params.period.text}`;
    }
  },

  "driver-performance": {
    name: "driver-performance",
    url: function(params) {
      return `driver-performance/${params.person.personId}`
        + `?from=${encodeURIComponent(formatDateTime(params.period.from, params.period.fromTime))}`
        + `&to=${encodeURIComponent(formatDateTime(params.period.to, params.period.toTime))}`
        + `&detailed=${params.detailed}`
        + `&daySummary=${params.daySummary}`
        + `&weekSummary=${params.weekSummary}`
        + `&borderCrossing=${params.borderCrossing}`
        + `&vehicleSummary=${params.vehicleSummary}`
        + `&showNote=${params.showNote}`;
    },
    filename: function(params) {
      return i18n.t("report.driver-performance.filename") + `-${params.person.firstName}-${params.person.lastName}-${params.period.text}`;
    }
  },

  temperature: {
    name: "temperature",
    url: function(params) {
      return `temperature/${params.vehicle.vehicleId}`
        + `?from=${encodeURIComponent(formatDateTime(params.period.from, params.period.fromTime))}`
        + `&to=${encodeURIComponent(formatDateTime(params.period.to, params.period.toTime))}`
        + `&granularity=${params.granularity}`
        + `&removeEmptyRows=${params.removeEmptyRows}`;
    },
    filename: function(params) {
      return i18n.t("report.temperature.filename") + `-${params.vehicle.numericPlate}-${params.period.text}`;
    }
  },

  tanks: {
    name: "tanks",
    url: function(params) {
      return `tanks/${params.vehicle.vehicleId}`
        + `?from=${encodeURIComponent(formatDateTime(params.period.from, params.period.fromTime))}`
        + `&to=${encodeURIComponent(formatDateTime(params.period.to, params.period.toTime))}`
        + `&detailed=${params.detailed}`
        + `&daySummary=${params.daySummary}`
        + `&weekSummary=${params.weekSummary}`
        + `&borderCrossing=${params.borderCrossing}`;
    },
    filename: function(params) {
      return i18n.t("report.tanks.filename") + `-${params.vehicle.numericPlate}-${params.period.text}`;
    }
  },

  tachograph: {
    name: "tachograph",
    url: function(params) {
      return `tachograph/${params.person.personId}?from=${encodeURIComponent(DateTime.fromISO(params.period.from).toFormat(utils.API_DFORMAT))}&to=${encodeURIComponent(DateTime.fromISO(params.period.to).toFormat(utils.API_DFORMAT))}&weeks=${params.weeks}&days=${params.days}`;
    },
    filename: function(params) {
      return i18n.t("report.tachograph.filename") + `-${params.person.firstName}-${params.person.lastName}-${params.period.text}`;
    }
  },

  "travel-expenses": {
    name: "travel_expenses",
    method: "POST",
    url: function(params) {
      if (params.vehicle !== null)
        return `travel-expenses/${params.person.personId}?vehicleId=${params.vehicle.vehicleId}&from=${encodeURIComponent(formatDateTime(params.period.from, params.period.fromTime))}&to=${encodeURIComponent(formatDateTime(params.period.to, params.period.toTime))}`;
      else
        return `travel-expenses/${params.person.personId}?from=${encodeURIComponent(formatDateTime(params.period.from, params.period.fromTime))}&to=${encodeURIComponent(formatDateTime(params.period.to, params.period.toTime))}`;
    },
    body: function(params) {
      return {
        ride_border: params.ride_border,
        home_as_abroad: params.home_as_abroad,
        simplified: params.simplified,
        place_from: params.place_from || null,
        place_to: params.place_to || null,
        purpose: params.purpose || null,
        days: params.days || null,
        deposit: utils.objectOrNull(params.deposit),
        deposit_date: params.deposit_date || null
      };
    },
    filename: function(params) {
      return i18n.t("report.travel-expenses.filename") + `-${params.person.firstName}-${params.person.lastName}-${params.period.text}`;
    }
  },

  "vehicles-summary": {
    name: "vehicles_summary",
    url: function(params) {
      if (params.groupset !== null)
        return `vehicles-summary?from=${encodeURIComponent(DateTime.fromISO(params.period.from).toFormat(utils.API_DFORMAT))}&to=${encodeURIComponent(DateTime.fromISO(params.period.to).toFormat(utils.API_DFORMAT))}&groupset=${params.groupset}`;
      else
        return `vehicles-summary?from=${encodeURIComponent(DateTime.fromISO(params.period.from).toFormat(utils.API_DFORMAT))}&to=${encodeURIComponent(DateTime.fromISO(params.period.to).toFormat(utils.API_DFORMAT))}`;
    },
    // eslint-disable-next-line no-unused-vars
    filename: function(params) {
      return i18n.t("report.vehicles-summary.filename") + "-" + params.period.text;
    }
  },

  "countries-summary": {
    name: "countries_summary",
    url: function(params) {
      const prefix = `countries-summary?from=${encodeURIComponent(DateTime.fromISO(params.period.from).toFormat(utils.API_DFORMAT))}&to=${encodeURIComponent(DateTime.fromISO(params.period.to).toFormat(utils.API_DFORMAT))}`;

      if (params.vehicles !== null && params.vehicles.length > 0)
        return prefix + `&vehicleIds=${params.vehicles.map(x => x.vehicleId).join(",")}`;
      if (params.groupset !== null)
        return prefix + `&groupset=${params.groupset}`;
      else
        return prefix;
    },
    // eslint-disable-next-line no-unused-vars
    filename: function(params) {
      return i18n.t("report.countries-summary.filename") + "-" + params.period.text;
    }
  },

  "abroad-summary": {
    name: "abroad_summary",
    url: function(params) {
      if (params.groupset !== null)
        return `abroad-summary?from=${encodeURIComponent(DateTime.fromISO(params.period.from).toFormat(utils.API_DFORMAT))}&to=${encodeURIComponent(DateTime.fromISO(params.period.to).toFormat(utils.API_DFORMAT))}&groupset=${params.groupset}`;
      else
        return `abroad-summary?from=${encodeURIComponent(DateTime.fromISO(params.period.from).toFormat(utils.API_DFORMAT))}&to=${encodeURIComponent(DateTime.fromISO(params.period.to).toFormat(utils.API_DFORMAT))}`;
    },
    // eslint-disable-next-line no-unused-vars
    filename: function(params) {
      return i18n.t("report.abroad-summary.filename") + "-" + params.period.text;
    }
  },

  "refuelings": {
    name: "refuelings",
    url: function(params) {
      const prefix = `refuelings?from=${encodeURIComponent(DateTime.fromISO(params.period.from).toFormat(utils.API_DFORMAT))}&to=${encodeURIComponent(DateTime.fromISO(params.period.to).toFormat(utils.API_DFORMAT))}`;

      if (params.vehicles !== null && params.vehicles.length > 0)
        return prefix + `&vehicleIds=${params.vehicles.map(x => x.vehicleId).join(",")}`;
      if (params.groupset !== null)
        return prefix + `&groupset=${params.groupset}`;
      else
        return prefix;
    },
    // eslint-disable-next-line no-unused-vars
    filename: function(params) {
      const filename = i18n.t("report.refuelings.filename");
      if (params.vehicles !== null && params.vehicles.length === 1)
        return filename + `-${params.vehicles[0].numericPlate}-${params.period.text}`;
      else
        return filename + `-${params.period.text}`;
    }
  },

  "planned-services": {
    name: "planned_services",
    url: function(params) {
      const prefix = `planned-services`;

      if (params.groupset !== null)
        return prefix + `?groupset=${params.groupset}`;
      else
        return prefix;
    },
    // eslint-disable-next-line no-unused-vars
    filename: function(params) {
      return i18n.t("report.planned-services.filename");
    }
  },

  "companies-summary": {
    name: "companies_summary",
    // eslint-disable-next-line no-unused-vars
    url: function(params) {
      return `companies-summary`;
    },
    // eslint-disable-next-line no-unused-vars
    filename: function(params) {
      return `companies-summary`;
    }
  }
};

const state = {
  /**
   * Hodnoty vstupních parametrů, které se pamatují při přepínaní mezi reporty
   */
  values: {}
};

const actions = {
  fetch({ dispatch }, params) {
    const def = defs[params.name];
    const output = outputs[params.output];

    if (def === null || def === undefined)
      return Promise.resolve(); // upravit na chybu

    let tid;

    // zavolame stazeni objektu, ktere az se provede, tak se objekt ulozi
    return dispatch("progress/add", "Fetch report", { root: true })
      .then(id => {
        tid = id;

        return axios({
          url: "/v1/{#company#}/report/" + def.url(params),
          headers: {
            "Accept": output.mimetype + ", application/json",
            "Accept-Language": i18n.locale
          },
          method: def.method || "GET",
          data: (typeof def.body === "function") ? def.body(params) : null,
          responseType: "blob"
        });
      }).then((response) => {
        if (params.download) {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          const fname = def.filename(params) + output.extension;
          link.href = url;
          link.setAttribute("download", fname);
          document.body.appendChild(link);
          link.click();
        } else {
          return response;
        }
      }).catch((error) => {
        return error;
      }).finally(() => {
        dispatch("progress/remove", tid, { root: true });
      });
  },

  setValue({ state }, params) {
    state.values[params.name] = params.value;
  }
};

const mutations = {};

export default {
  namespaced: true,
  state,
  actions,
  mutations
};